import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  ArrowRight, ArrowLeft, Check, Truck, Trash2, Sparkles, Boxes, Recycle, Hammer, Package, Layers,
  Sun, Sunrise, Moon, Plus, Minus, Camera, MessageSquare,
  PackageOpen, Wrench, MoveVertical, Car, HelpCircle,
  Zap, CalendarDays, CalendarRange, CalendarPlus, Infinity as InfinityIcon,
} from "lucide-react";
import { toast } from "sonner";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { useI18n } from "@/lib/i18n";
import { useAuth } from "@/lib/auth";
import PostalCodeInput from "@/components/PostalCodeInput";
import DatePicker from "@/components/DatePicker";
import PhotoUpload from "@/components/PhotoUpload";
import { createQuote } from "@/lib/api";
import { ext as qext, whatsappUrl } from "@/lib/quoteI18nExt";
import Seo from "@/components/Seo";
import { useExtraT } from "@/lib/extraI18n";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";

const SERVICE_ICONS = {
  move: Truck, clear: Trash2, declutter: Layers, organize: Boxes, clean: Sparkles,
  recycle: Recycle, assembly: Hammer, transport: Package, full: Sparkles,
};
const SERVICE_KEYS = ["move", "clear", "clean", "transport", "full"];
const TIME_SLOT_KEYS = ["morning", "afternoon", "evening", "any"];
const SLOT_ICONS = { morning: Sunrise, afternoon: Sun, evening: Moon, any: Check };
const URGENCY_KEYS = ["asap", "soon", "month", "flex", "precise"];

export default function Quote() {
  const { user } = useAuth();
  const { lang } = useI18n();
  const x = useExtraT();
  const navigate = useNavigate();
  const search = new URLSearchParams(useLocation().search);
  const urlService = search.get("service");
  const urlZip = search.get("zip") || "";

  const SERVICES = SERVICE_KEYS.reduce((acc, k) => {
    acc[k] = { Icon: SERVICE_ICONS[k], label: x.qf.services[k].label, cta: x.qf.services[k].cta };
    return acc;
  }, {});
  const TIME_SLOTS = TIME_SLOT_KEYS.map((id) => ({ id, label: x.qf.slots[id].label, hint: x.qf.slots[id].hint, Icon: SLOT_ICONS[id] }));
  const xe = qext(lang || "fr");
  const URGENCY = URGENCY_KEYS.map((id) => {
    if (id === "precise") return { id, label: xe.precise, hint: "" };
    const u = x.qf.urgency[id] || {};
    return { id, label: u.label || id, hint: u.hint || "" };
  });
  const CLIENT_TYPE = [{ id: "particulier", label: x.qf.clientType.particulier }, { id: "professionnel", label: x.qf.clientType.professionnel }];

  const [step, setStep] = useState(urlService && SERVICES[urlService] ? 1 : 0);
  const [service, setService] = useState(urlService && SERVICES[urlService] ? urlService : "");
  const [d, setD] = useState({
    clientType: search.get("type") === "pro" ? "professionnel" : "particulier",
    // Move/Transport
    addressFrom: "", addressTo: "", rooms: 0,
    floorFrom: 0, floorTo: 0, liftFrom: "no", liftTo: "no",
    needBoxes: false, needDisassembly: false, needCleaning: false,
    // Clear
    spaceType: "", heavyItems: false, cleanAfter: false,
    // Declutter/Organize
    targetRoom: "", goal: "", needStorageBuy: false, needDeclutterFirst: false,
    // Clean
    cleanType: "", surface: "", finalLease: false, windows: false, appliances: false,
    // Recycle
    itemsType: "",
    // Assembly
    assemblyKind: "assembly", brand: "ikea", itemCount: 1,
    // Transport
    itemDescription: "", dimensions: "",
    // Full
    fullSubservices: [],
    // Urgency + free message
    urgency: "soon",
    message: "",
    // Photos uploaded by client (array of photo IDs)
    photo_ids: [],
    // New per-service qualification (enhancement 2026-02)
    homeSize: "",           // move: studio/p2/p3/p4/p5/house/pro/idk
    moveExtras: [],         // move: boxes/packing/dis/clean/clear
    accessConstraint: "",   // move: easy/narrow/parking/idk
    clearVolume: "",        // clear: few/half/one/several/full/idk
    clearSituation: "",     // clear: succession/lease/ems/packed/afterMove/none
    heavyChoice: "no",      // yes/no/idk
    cleanOptions: [],       // clean: windows/appliances/balcony/cellar/handover
    cleanState: "",         // clean: normal/dirty/empty/furnished/idk
    organizeScope: "",      // organize: room/multi/full/idk
    transportItem: "",      // transport: sofa/bed/wardrobe/table/appliance/multi/other
    transportHeavy: "no",   // yes/no/idk
    transportDis: "no",     // yes/no/talk
  });
  const [contact, setContact] = useState({
    firstName: "", lastName: "", email: "", phone: "",
    companyName: "",
    address: "", postalCode: urlZip, date: "", timeSlot: "any",
    // NEW: callback + method + opt-in + combined
    callback: "any",
    contactMethod: "phone",
    optIn: false,
    combinedServices: [],
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => { window.scrollTo({ top: 0, behavior: "smooth" }); }, [step]);
  useEffect(() => {
    if (!user) return;
    const parts = (user.name || "").trim().split(/\s+/);
    setContact((p) => ({
      ...p,
      firstName: p.firstName || parts[0] || "",
      lastName: p.lastName || parts.slice(1).join(" ") || "",
      email: p.email || user.email || "",
      phone: p.phone || user.phone || "",
      address: p.address || user.address || "",
      postalCode: p.postalCode || user.postal_code || "",
    }));
  }, [user]);

  const totalSteps = service ? 3 : 4; // 0:service, 1:details, 2:contact, 3:summary

  function setField(k, v) { setD((p) => ({ ...p, [k]: v })); }
  function setContactField(k, v) { setContact((p) => ({ ...p, [k]: v })); }
  function toggleFull(k) { setField("fullSubservices", d.fullSubservices.includes(k) ? d.fullSubservices.filter((s) => s !== k) : [...d.fullSubservices, k]); }

  function validateContact() {
    // Reduced-obligatory schema (enhancement 2026-02): only first name, phone, postal code, callback preference, and opt-in are strictly required.
    if (!contact.firstName.trim()) { toast.error(x.common.contactNameRequired); return false; }
    if (!contact.phone.trim()) { toast.error(x.common.contactPhoneRequired); return false; }
    if (!contact.postalCode || !contact.postalCode.trim()) { toast.error(x.common.contactAddressRequired); return false; }
    if (!contact.callback) { toast.error(x.common.contactPhoneRequired); return false; }
    if (!contact.optIn) { toast.error(lang === "de" ? "Bitte kreuzen Sie das Kästchen an, um kontaktiert zu werden." : lang === "en" ? "Please tick the box to be contacted back." : "Merci de cocher la case pour être recontacté."); return false; }
    // Pro-only: email is strictly required for professional clients (B2B follow-up).
    if (d.clientType === "professionnel") {
      if (!contact.email || !contact.email.trim()) { toast.error(x.common.contactEmailRequired || "L'email est requis pour les professionnels."); return false; }
      if (!contact.email.includes("@")) { toast.error(x.common.emailInvalid); return false; }
    }
    // Email/last-name/address remain optional but validated only if provided
    if (contact.email && contact.email.trim() && !contact.email.includes("@")) { toast.error(x.common.emailInvalid); return false; }
    return true;
  }

  async function submit() {
    if (!validateContact()) { setStep(2); return; }
    setSubmitting(true);
    try {
      const payload = buildPayload(service, d, contact, lang);
      const created = await createQuote(payload);
      try {
        const { sendEvent } = await import("@/lib/liveTracker");
        sendEvent("quote_submitted", {
          quote_id: created?.id,
          name: [contact.firstName, contact.lastName].filter(Boolean).join(" ") || contact.name,
          service,
        });
      } catch { /* noop */ }
      setSubmitted(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      toast.error(e?.response?.data?.detail || x.common.sendingError);
    } finally {
      setSubmitting(false);
    }
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-[#F7F7F8]">
        <Header />
        <main className="pt-32 pb-20 px-4">
          <div className="max-w-2xl mx-auto rounded-3xl bg-white p-10 md:p-12 text-center" data-testid="quote-thanks">
            <div className="mx-auto w-14 h-14 rounded-full bg-emerald-100 text-emerald-700 inline-flex items-center justify-center">
              <Check size={26} strokeWidth={3} />
            </div>
            <h1 className="mt-6 text-3xl md:text-4xl font-extrabold tracking-tight text-[#0A0F2C]" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
              {x.qf.thanks}
            </h1>
            <p className="mt-3 text-[#4B5563]">{x.qf.thanksSub} <span className="font-semibold text-[#0A0F2C]">{contact.email}</span>{x.qf.thanksSub2}</p>
            <button onClick={() => navigate("/")} className="mt-8 inline-flex items-center gap-2 rounded-full bg-[#0A0F2C] hover:bg-[#2D5BFF] text-white px-6 py-3 font-bold text-sm transition-all" data-testid="quote-thanks-home">
              {x.qf.thanksHome}
            </button>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const cur = SERVICES[service];

  return (
    <div className="min-h-screen bg-[#F7F7F8]">
      <Seo
        title={lang === "de" ? "Angebot für Umzug oder Entrümpelung anfordern | Mopper" : lang === "en" ? "Request a moving or clear-out quote | Mopper" : "Demander un devis de déménagement ou débarras | Mopper"}
        description={lang === "de" ? "Beschreiben Sie Ihren Bedarf und erhalten Sie kostenlos ein Mopper-Angebot für Umzug, Entrümpelung, Reinigung oder Möbeltransport." : lang === "en" ? "Describe your need and get a free Mopper quote for a move, a clear-out, cleaning or furniture transport." : "Décrivez votre besoin et recevez gratuitement un devis Mopper pour un déménagement, un débarras, un nettoyage ou un transport de meubles."}
        path="/devis"
        noindex={false}
      />
      <Header />
      <main className="pt-24 md:pt-28 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto">
          {/* SEO H1 (visible AT for accessibility, layout neutral) */}
          <h1 className="sr-only">{lang === "de" ? "Fordern Sie Ihr kostenloses Angebot an" : lang === "en" ? "Request your free quote" : "Demandez votre devis gratuit"}</h1>
          {/* Progress */}
          <ProgressBar
            step={service ? step - 1 : step}
            total={totalSteps}
            labels={service ? [xe.steps.details, xe.steps.contact, xe.steps.summary] : [xe.steps.service, xe.steps.details, xe.steps.contact, xe.steps.summary]}
          />

          {/* Step 0: pick service */}
          {step === 0 && (
            <Card>
              <SectionTitle eyebrow={x.qf.sectionService} title={x.qf.pickService} sub={x.qf.pickServiceSub} />
              <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 gap-3">
                {Object.entries(SERVICES).map(([k, s]) => (
                  <ChoiceCard key={k} active={service === k} onClick={() => { setService(k); setStep(1); }} Icon={s.Icon} label={s.label} testId={`quote-pick-${k}`} />
                ))}
              </div>
            </Card>
          )}

          {/* Step 1: service-specific details */}
          {step === 1 && cur && (
            <Card>
              <div className="flex items-center gap-3 mb-1">
                <span className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-[#EAF0FF] text-[#2D5BFF]"><cur.Icon size={18} /></span>
                <span className="text-xs uppercase tracking-[0.18em] text-[#2D5BFF] font-bold">{cur.label}</span>
              </div>
              <SectionTitle title={titleForService(service, x)} sub={subForService(service, x)} />

              <div className="mt-6 space-y-6">
                <Block label={x.qf.youAre}>
                  <SegControl options={CLIENT_TYPE} value={d.clientType} onChange={(v) => setField("clientType", v)} testIdPrefix="quote-client-type" />
                </Block>

                {service === "move" && <MoveFields d={d} setField={setField} x={x} xe={xe} />}
                {service === "transport" && <TransportFields d={d} setField={setField} x={x} xe={xe} />}
                {service === "clear" && <ClearFields d={d} setField={setField} x={x} xe={xe} />}
                {service === "declutter" && <DeclutterFields d={d} setField={setField} x={x} />}
                {service === "organize" && <OrganizeFields d={d} setField={setField} x={x} xe={xe} />}
                {service === "clean" && <CleanFields d={d} setField={setField} x={x} xe={xe} />}
                {service === "recycle" && <RecycleFields d={d} setField={setField} x={x} />}
                {service === "assembly" && <AssemblyFields d={d} setField={setField} x={x} />}
                {service === "full" && <FullFields d={d} toggleFull={toggleFull} x={x} />}

                <Block label={x.qf.when}>
                  {(() => {
                    const URGENCY_ICONS = { asap: Zap, soon: CalendarDays, month: CalendarRange, flex: InfinityIcon };
                    // Compact labels — keep same IDs, just tighter wording
                    const URGENCY_COMPACT = {
                      fr: { asap: "48–72h", soon: "Cette semaine", month: "Ce mois-ci", flex: "Flexible" },
                      de: { asap: "48–72h", soon: "Diese Woche", month: "Diesen Monat", flex: "Flexibel" },
                      en: { asap: "48–72h", soon: "This week", month: "This month", flex: "Flexible" },
                    };
                    const compactMap = URGENCY_COMPACT[lang] || URGENCY_COMPACT.fr;
                    const URGENCY_SIMPLE = URGENCY.filter((u) => u.id !== "precise").map((u) => ({ ...u, label: compactMap[u.id] || u.label }));
                    const isPrecise = d.urgency === "precise";
                    return (
                      <>
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="quote-urgency-grid">
                          {URGENCY_SIMPLE.map((u) => {
                            const Ic = URGENCY_ICONS[u.id];
                            const on = d.urgency === u.id;
                            return (
                              <button
                                key={u.id} type="button"
                                onClick={() => setField("urgency", u.id)}
                                data-testid={`quote-urgency-${u.id}`}
                                className={`flex flex-col items-center justify-center gap-1 rounded-xl border transition-all py-3 px-2
                                  ${on ? "border-[#2D5BFF] bg-[#EAF0FF] text-[#0A0F2C]" : "border-gray-200 bg-white text-[#4B5563] hover:border-gray-300"}`}
                              >
                                {Ic && <Ic size={16} className={on ? "text-[#2D5BFF]" : "text-[#9CA3AF]"} />}
                                <div className="text-xs font-bold text-center leading-tight">{u.label}</div>
                              </button>
                            );
                          })}
                        </div>
                        <div className="mt-3">
                          <button
                            type="button"
                            onClick={() => setField("urgency", isPrecise ? "soon" : "precise")}
                            data-testid="quote-urgency-precise-toggle"
                            className={`inline-flex items-center gap-1.5 text-xs font-bold transition-colors ${isPrecise ? "text-[#2D5BFF]" : "text-[#4B5563] hover:text-[#0A0F2C]"}`}
                          >
                            <CalendarPlus size={13} />
                            <span className="underline underline-offset-2">{isPrecise ? (lang === "de" ? "Option oben verwenden" : lang === "en" ? "Use an option above" : "Utiliser une option ci-dessus") : (lang === "de" ? "Genaues Datum wählen" : lang === "en" ? "Choose a specific date" : "Choisir une date précise")}</span>
                          </button>
                          {isPrecise && (
                            <div className="mt-3">
                              <DatePicker value={contact.date} onChange={(v) => setContactField("date", v)} placeholder={x.qf.datePh} testId="quote-precise-date" />
                            </div>
                          )}
                        </div>
                      </>
                    );
                  })()}
                </Block>

                {/* Combined services — visible only for "full" service to highlight multi-service offering */}
                {/* NOTE: The "full" service already collects the subservices via FullFields (d.fullSubservices).
                    A duplicate top-level "combined services" block used to live here — removed to avoid asking the same question twice. */}

                <Block label={x.qf.precision} hint={x.qf.precisionHint}>
                  <Textarea value={d.message} onChange={(v) => setField("message", v)} placeholder={x.qf.precisionPh} testId="quote-message" />
                </Block>

                <Block label={xe.photosCta} hint={xe.photosCtaHint}>
                  <PhotoUpload value={d.photo_ids || []} onChange={(ids) => setField("photo_ids", ids)} i18n={{
                    addPhotos: lang === "de" ? "Fotos hinzufügen" : lang === "en" ? "Add photos" : "Ajouter des photos",
                    hint: lang === "de" ? "Akzeptierte Formate: JPG, PNG, HEIC. Max. 12 Fotos. Automatisch komprimiert." : lang === "en" ? "Accepted formats: JPG, PNG, HEIC. Max 12 photos. Automatically compressed." : "Formats acceptés : JPG, PNG, HEIC. Max 12 photos. Compressées automatiquement.",
                    uploading: lang === "de" ? "Wird hochgeladen…" : lang === "en" ? "Uploading…" : "Envoi en cours…",
                    remove: lang === "de" ? "Entfernen" : lang === "en" ? "Remove" : "Retirer",
                    error: lang === "de" ? "Foto konnte nicht gesendet werden" : lang === "en" ? "Could not upload this photo" : "Impossible d'envoyer cette photo",
                  }} />
                </Block>
              </div>

              <NavBtns onBack={() => { setService(""); setStep(0); }} onNext={() => setStep(2)} backLabel={x.qf.changeService} nextLabel={x.common.continue} testIdPrefix="quote-step1" />
            </Card>
          )}

          {/* Step 2: contact + date + time */}
          {step === 2 && cur && (
            <Card>
              <SectionTitle eyebrow={x.qf.sectionContact} title={x.qf.contactTitle} sub={x.qf.contactSub} />
              <div className="mt-6 space-y-6">
                <Grid2>
                  <Field label={x.qf.firstName} required value={contact.firstName} onChange={(v) => setContactField("firstName", v)} testId="quote-firstName" autoFocus />
                  <Field label={x.qf.lastName} value={contact.lastName} onChange={(v) => setContactField("lastName", v)} testId="quote-lastName" />
                </Grid2>
                {d.clientType === "professionnel" && (
                  <Field
                    label={x.qf.companyName || "Nom de l'entreprise"}
                    value={contact.companyName}
                    onChange={(v) => setContactField("companyName", v)}
                    placeholder={x.qf.companyNamePh || "Ex : Mopper SA"}
                    testId="quote-companyName"
                  />
                )}
                <Grid2>
                  <Field label={x.qf.phone} required type="tel" value={contact.phone} onChange={(v) => setContactField("phone", v)} placeholder={x.qf.contactFields.phonePh} testId="quote-phone" />
                  <Field label={x.qf.email} required={d.clientType === "professionnel"} type="email" value={contact.email} onChange={(v) => setContactField("email", v)} placeholder={x.qf.contactFields.emailPh} testId="quote-email" />
                </Grid2>
                <Field label={x.qf.address} value={contact.address} onChange={(v) => setContactField("address", v)} placeholder={x.qf.addressPh} testId="quote-address" />
                <Block label={`${x.qf.postal} *`}>
                  <PostalCodeInput value={contact.postalCode} onChange={(v) => setContactField("postalCode", v)} placeholder={x.qf.contactFields.postalPh} inputClassName="w-full h-12 rounded-xl border border-gray-200 bg-white px-4 text-[#0A0F2C] focus:ring-2 focus:ring-[#2D5BFF] outline-none" testId="quote-postal" />
                </Block>
                <Block label={x.qf.date} hint={x.qf.dateHint}>
                  <DatePicker value={contact.date} onChange={(v) => setContactField("date", v)} placeholder={x.qf.datePh} testId="quote-date" />
                </Block>
                <Block label={x.qf.slot}>
                  <Grid2>
                    {TIME_SLOTS.map((tt) => (
                      <Pill key={tt.id} active={contact.timeSlot === tt.id} onClick={() => setContactField("timeSlot", tt.id)} testId={`quote-slot-${tt.id}`}>
                        <div className="flex items-center gap-2">
                          <tt.Icon size={16} />
                          <div>
                            <div className="font-bold text-sm">{tt.label}</div>
                            <div className="text-xs opacity-70">{tt.hint}</div>
                          </div>
                        </div>
                      </Pill>
                    ))}
                  </Grid2>
                </Block>

                {/* NEW: Callback preference */}
                <Block label={xe.callback.label} hint={xe.callback.hint}>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    {Object.entries(xe.callback.options).map(([id, label]) => (
                      <Pill key={id} active={contact.callback === id} onClick={() => setContactField("callback", id)} testId={`quote-callback-${id}`}>
                        <div className="font-bold text-sm">{label}</div>
                      </Pill>
                    ))}
                  </div>
                </Block>

                {/* NEW: Contact method */}
                <Block label={xe.contactMethod.label}>
                  <div className="grid grid-cols-3 gap-2">
                    {Object.entries(xe.contactMethod.options).map(([id, label]) => (
                      <Pill key={id} active={contact.contactMethod === id} onClick={() => setContactField("contactMethod", id)} testId={`quote-method-${id}`}>
                        <div className="font-bold text-sm text-center">{label}</div>
                      </Pill>
                    ))}
                  </div>
                </Block>

                {/* NEW: opt-in */}
                <label className="flex items-start gap-2 bg-[#F7F7F8] rounded-xl px-3 py-3 cursor-pointer" data-testid="quote-optin-wrap">
                  <input type="checkbox" checked={contact.optIn} onChange={(e) => setContactField("optIn", e.target.checked)} className="mt-0.5 accent-[#2D5BFF]" data-testid="quote-optin" />
                  <div>
                    <div className="text-sm font-bold text-[#0A0F2C]">{xe.optIn}</div>
                    <div className="text-xs text-[#4B5563]">{xe.optInHint}</div>
                  </div>
                </label>
                <div className="text-[11px] text-[#9CA3AF]">{xe.fieldsRequired}</div>
              </div>
              <NavBtns onBack={() => setStep(1)} onNext={() => { if (validateContact()) setStep(3); }} testIdPrefix="quote-step2" backLabel={x.common.back} nextLabel={x.common.continue} />
            </Card>
          )}

          {/* Step 3: summary */}
          {step === 3 && cur && (
            <Card>
              <SectionTitle eyebrow={x.qf.sectionSummary} title={x.qf.summaryTitle} sub={x.qf.summarySub} />
              <div className="mt-4 p-4 rounded-2xl bg-[#EAF0FF] border border-[#2D5BFF]/15 text-sm text-[#0A0F2C] leading-relaxed" data-testid="quote-submit-reassure">
                {xe.submitReassure}
              </div>
              <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-5">
                <Row k={x.qf.summaryService} v={cur.label} />
                <Row k={x.qf.summaryProfile} v={d.clientType === "particulier" ? x.qf.clientType.particulier : x.qf.clientType.professionnel} />
                {d.clientType === "professionnel" && contact.companyName && <Row k={x.qf.companyName || "Nom de l'entreprise"} v={contact.companyName} />}
                {summaryRows(service, d, x, SERVICES).map((r) => r.v && <Row key={r.k} k={r.k} v={r.v} />)}
                <Row k={x.qf.summaryWhen} v={URGENCY.find((u) => u.id === d.urgency)?.label} />
                <Row k={x.qf.summaryContact} v={`${contact.firstName} ${contact.lastName}`} />
                <Row k={x.qf.phone} v={contact.phone} />
                {contact.email && <Row k={x.qf.email} v={contact.email} />}
                <Row k={x.qf.summaryAddress} v={`${contact.address || "—"}${contact.postalCode ? `, ${contact.postalCode}` : ""}`} className="md:col-span-2" />
                {contact.date && <Row k={x.qf.summaryDate} v={contact.date} />}
                <Row k={x.qf.summarySlot} v={TIME_SLOTS.find((tt) => tt.id === contact.timeSlot)?.label} />
                <Row k={xe.callback.label} v={xe.callback.options[contact.callback]} />
                <Row k={xe.contactMethod.label} v={xe.contactMethod.options[contact.contactMethod]} />
                {(contact.combinedServices || []).length > 0 && <Row k={xe.full.title} v={contact.combinedServices.map((k) => xe.full.options[k]).join(" · ")} className="md:col-span-2" />}
                {(d.photo_ids || []).length > 0 && <Row k="Photos" v={`${d.photo_ids.length} photo${d.photo_ids.length > 1 ? "s" : ""}`} />}
                {d.message && <Row k={x.qf.noteLabel} v={d.message} className="md:col-span-2" />}
              </div>
              <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-between">
                <button onClick={() => setStep(2)} className="inline-flex items-center gap-2 rounded-full bg-gray-100 hover:bg-gray-200 text-[#0A0F2C] px-6 py-3 font-bold transition-all" data-testid="quote-back-summary">
                  <ArrowLeft size={16} /> {x.common.back}
                </button>
                <button onClick={submit} disabled={submitting} className="inline-flex items-center justify-center gap-2 rounded-full bg-[#2D5BFF] hover:bg-[#0A0F2C] text-white px-7 py-4 font-bold disabled:opacity-60 transition-all" data-testid="quote-submit">
                  {submitting ? x.common.sending : cur.cta} <ArrowRight size={16} />
                </button>
              </div>
            </Card>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}

/* ---------- Service-specific blocks ---------- */
function MoveFields({ d, setField, x, xe }) {
  const f = x.qf.f;
  const EXTRA_ICONS = { boxes: Package, packing: PackageOpen, dis: Wrench, clean: Sparkles, clear: Trash2 };
  const ACCESS_ICONS = { easy: Check, narrow: MoveVertical, parking: Car, idk: HelpCircle };
  const extras = Object.entries(xe.move.extras);
  const access = Object.entries(xe.move.access);
  const toggleExtra = (id) => {
    const arr = d.moveExtras || [];
    setField("moveExtras", arr.includes(id) ? arr.filter((v) => v !== id) : [...arr, id]);
  };
  const toggleAccess = (id) => {
    const arr = Array.isArray(d.accessConstraint) ? d.accessConstraint : (d.accessConstraint ? [d.accessConstraint] : []);
    setField("accessConstraint", arr.includes(id) ? arr.filter((v) => v !== id) : [...arr, id]);
  };
  const accessSel = Array.isArray(d.accessConstraint) ? d.accessConstraint : (d.accessConstraint ? [d.accessConstraint] : []);
  return (
    <>
      <Block label={f.addrFrom}><Field value={d.addressFrom} onChange={(v) => setField("addressFrom", v)} placeholder={f.addrPh} testId="quote-move-from" /></Block>
      <Block label={f.addrTo}><Field value={d.addressTo} onChange={(v) => setField("addressTo", v)} placeholder={f.addrPh} testId="quote-move-to" /></Block>
      <Grid2>
        <Block label={f.rooms}><Stepper value={d.rooms} onChange={(v) => setField("rooms", v)} step={0.5} min={0} max={30} testId="quote-move-rooms" /></Block>
        <Block label={f.volume}><Field value={d.surface} onChange={(v) => setField("surface", v)} placeholder="25" testId="quote-move-surface" inputMode="numeric" /></Block>
      </Grid2>
      <Grid2>
        <Block label={f.floorFrom}><Stepper value={d.floorFrom} onChange={(v) => setField("floorFrom", v)} testId="quote-move-floor-from" /></Block>
        <Block label={f.floorTo}><Stepper value={d.floorTo} onChange={(v) => setField("floorTo", v)} testId="quote-move-floor-to" /></Block>
      </Grid2>
      <Grid2>
        <Block label={f.liftFrom}><SegControl options={[{id:"yes",label:x.common.yes},{id:"no",label:x.common.no}]} value={d.liftFrom} onChange={(v) => setField("liftFrom", v)} testIdPrefix="quote-move-lift-from" /></Block>
        <Block label={f.liftTo}><SegControl options={[{id:"yes",label:x.common.yes},{id:"no",label:x.common.no}]} value={d.liftTo} onChange={(v) => setField("liftTo", v)} testIdPrefix="quote-move-lift-to" /></Block>
      </Grid2>
      <Block label={xe.move.extraLabel}>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="quote-move-extras">
          {extras.map(([id, label]) => (
            <OptionRow key={id} icon={EXTRA_ICONS[id]} label={label} active={(d.moveExtras || []).includes(id)} onClick={() => toggleExtra(id)} testId={`quote-move-extra-${id}`} />
          ))}
        </div>
      </Block>
      <Block label={xe.move.accessLabel}>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="quote-move-access">
          {access.map(([id, label]) => (
            <OptionRow key={id} icon={ACCESS_ICONS[id]} label={label} active={accessSel.includes(id)} onClick={() => toggleAccess(id)} testId={`quote-move-access-${id}`} />
          ))}
        </div>
      </Block>
    </>
  );
}

function DDSelect({ value, onChange, options, placeholder, testId }) {
  return (
    <Select value={value || ""} onValueChange={onChange}>
      <SelectTrigger className="h-14 rounded-2xl border border-gray-200 bg-white px-5 text-[#0A0F2C] font-semibold" data-testid={testId}>
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent className="rounded-2xl">
        {options.map(([id, label]) => (
          <SelectItem key={id} value={id} data-testid={`${testId}-opt-${id}`}>{label}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}


function TransportFields({ d, setField, x, xe }) {
  const f = x.qf.f;
  const items = Object.entries(xe.transport.items);
  return (
    <>
      <Block label={xe.transport.itemLabel}>
        <DDSelect value={d.transportItem} onChange={(v) => setField("transportItem", v)} options={items} placeholder={x.qf.choosePh || "…"} testId="quote-tr-item" />
      </Block>
      <Block label={f.itemTr} hint={f.specifyOptional}><Field value={d.itemDescription} onChange={(v) => setField("itemDescription", v)} placeholder={f.itemTrPh} testId="quote-tr-desc" /></Block>
      <Grid2>
        <Block label={f.depart}><Field value={d.addressFrom} onChange={(v) => setField("addressFrom", v)} placeholder={f.addr} testId="quote-tr-from" /></Block>
        <Block label={f.arrive}><Field value={d.addressTo} onChange={(v) => setField("addressTo", v)} placeholder={f.addr} testId="quote-tr-to" /></Block>
      </Grid2>
      <Grid2>
        <Block label={f.floorFrom}><Stepper value={d.floorFrom} onChange={(v) => setField("floorFrom", v)} testId="quote-tr-floor-from" /></Block>
        <Block label={f.floorTo}><Stepper value={d.floorTo} onChange={(v) => setField("floorTo", v)} testId="quote-tr-floor-to" /></Block>
      </Grid2>
      <Grid2>
        <Block label={f.liftFrom}><SegControl options={[{id:"yes",label:x.common.yes},{id:"no",label:x.common.no}]} value={d.liftFrom} onChange={(v) => setField("liftFrom", v)} testIdPrefix="quote-tr-lift-from" /></Block>
        <Block label={f.liftTo}><SegControl options={[{id:"yes",label:x.common.yes},{id:"no",label:x.common.no}]} value={d.liftTo} onChange={(v) => setField("liftTo", v)} testIdPrefix="quote-tr-lift-to" /></Block>
      </Grid2>
      <Block label={xe.transport.weightLabel}>
        <SegControl options={[{id:"yes",label:xe.yn.yes},{id:"no",label:xe.yn.no},{id:"idk",label:xe.yn.idk}]} value={d.transportHeavy} onChange={(v) => setField("transportHeavy", v)} testIdPrefix="quote-tr-heavy" />
      </Block>
      <Block label={xe.transport.disLabel}>
        <SegControl options={[{id:"yes",label:xe.transport.disYes},{id:"no",label:xe.transport.disNo},{id:"talk",label:xe.transport.disTalk}]} value={d.transportDis} onChange={(v) => setField("transportDis", v)} testIdPrefix="quote-tr-dis-choice" />
      </Block>
    </>
  );
}

function ClearFields({ d, setField, x, xe }) {
  const f = x.qf.f;
  const spaces = [
    { id: "cave", label: f.spaces.cave },
    { id: "grenier", label: f.spaces.grenier },
    { id: "garage", label: f.spaces.garage },
    { id: "appartement", label: f.spaces.appartement },
    { id: "maison", label: f.spaces.maison },
    { id: "bureau", label: f.spaces.bureau },
  ];
  const situations = Object.entries(xe.clear.situations);
  const choosePh = x.qf.choosePh || "…";
  return (
    <>
      <Block label={f.spaceType}>
        <Select value={d.spaceType || ""} onValueChange={(v) => setField("spaceType", v)}>
          <SelectTrigger className="h-14 rounded-2xl border border-gray-200 bg-white px-5 text-[#0A0F2C] font-semibold" data-testid="quote-clear-space-select">
            <SelectValue placeholder={choosePh} />
          </SelectTrigger>
          <SelectContent className="rounded-2xl">
            {spaces.map((o) => (
              <SelectItem key={o.id} value={o.id} data-testid={`quote-clear-space-opt-${o.id}`}>{o.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </Block>
      <Block label={xe.clear.situationLabel}>
        <Select value={d.clearSituation || ""} onValueChange={(v) => setField("clearSituation", v)}>
          <SelectTrigger className="h-14 rounded-2xl border border-gray-200 bg-white px-5 text-[#0A0F2C] font-semibold" data-testid="quote-clear-situation-select">
            <SelectValue placeholder={choosePh} />
          </SelectTrigger>
          <SelectContent className="rounded-2xl">
            {situations.map(([id, label]) => (
              <SelectItem key={id} value={id} data-testid={`quote-clear-situation-opt-${id}`}>{label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </Block>
      <Grid2>
        <Block label={f.volApprox}><Field value={d.surface} onChange={(v) => setField("surface", v)} placeholder="15" testId="quote-clear-surface" inputMode="numeric" /></Block>
        <Block label={f.floor}><Stepper value={d.floorFrom} onChange={(v) => setField("floorFrom", v)} testId="quote-clear-floor" /></Block>
      </Grid2>
      <Block label={f.liftAvail}><SegControl options={[{id:"yes",label:x.common.yes},{id:"no",label:x.common.no},{id:"idk",label:xe.yn.idk}]} value={d.liftFrom} onChange={(v) => setField("liftFrom", v)} testIdPrefix="quote-clear-lift" /></Block>
      <Block label={xe.clear.heavyLabel}>
        <SegControl options={[{id:"yes",label:xe.yn.yes},{id:"no",label:xe.yn.no},{id:"idk",label:xe.yn.idk}]} value={d.heavyChoice} onChange={(v) => setField("heavyChoice", v)} testIdPrefix="quote-clear-heavy-choice" />
      </Block>
      <Block label={f.precisions}>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          <OptionRow icon={Sparkles} label={f.cleanAfter} active={!!d.cleanAfter} onClick={() => setField("cleanAfter", !d.cleanAfter)} testId="quote-clear-clean-after" />
        </div>
      </Block>
    </>
  );
}

function DeclutterFields({ d, setField, x }) {
  const f = x.qf.f;
  return (
    <>
      <Block label={f.targetRoom}><Field value={d.targetRoom} onChange={(v) => setField("targetRoom", v)} placeholder={f.targetRoomPh} testId="quote-dec-room" /></Block>
      <Block label={f.goal}><Textarea value={d.goal} onChange={(v) => setField("goal", v)} placeholder={f.goalPh} testId="quote-dec-goal" /></Block>
    </>
  );
}

function OrganizeFields({ d, setField, x, xe }) {
  const f = x.qf.f;
  const scopes = Object.entries(xe.organize.scopes);
  const goals = Object.entries(xe.organize.goals);
  return (
    <>
      <Block label={xe.organize.scopeLabel}>
        <DDSelect value={d.organizeScope} onChange={(v) => setField("organizeScope", v)} options={scopes} placeholder={x.qf.choosePh || "…"} testId="quote-org-scope" />
      </Block>
      <Block label={f.roomOrg}><Field value={d.targetRoom} onChange={(v) => setField("targetRoom", v)} placeholder={f.roomOrgPh} testId="quote-org-room" /></Block>
      <Block label={xe.organize.goalLabel}>
        <DDSelect value={d.goal} onChange={(v) => setField("goal", v)} options={goals} placeholder={x.qf.choosePh || "…"} testId="quote-org-goal" />
      </Block>
      <Block label={x.qf.f.addNeeds} hint={x.qf.f.selectMultiple}>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          <OptionRow icon={Boxes} label={f.needStorage} active={!!d.needStorageBuy} onClick={() => setField("needStorageBuy", !d.needStorageBuy)} testId="quote-org-need-storage" />
          <OptionRow icon={Trash2} label={f.needDecFirst} active={!!d.needDeclutterFirst} onClick={() => setField("needDeclutterFirst", !d.needDeclutterFirst)} testId="quote-org-need-declutter" />
        </div>
      </Block>
    </>
  );
}

function CleanFields({ d, setField, x, xe }) {
  const f = x.qf.f;
  const types = Object.entries(xe.clean.types);
  const options = Object.entries(xe.clean.options);
  const states = Object.entries(xe.clean.states);
  const toggleOpt = (id) => {
    const arr = d.cleanOptions || [];
    setField("cleanOptions", arr.includes(id) ? arr.filter((x) => x !== id) : [...arr, id]);
  };
  return (
    <>
      <Block label={xe.clean.typeLabel}>
        <DDSelect value={d.cleanType} onChange={(v) => setField("cleanType", v)} options={types} placeholder={x.qf.choosePh || "…"} testId="quote-clean-type" />
      </Block>
      <Grid2>
        <Block label={f.surface}><Field value={d.surface} onChange={(v) => setField("surface", v)} placeholder="80" testId="quote-clean-surface" inputMode="numeric" /></Block>
        <Block label={f.nbRooms}><Stepper value={d.rooms} onChange={(v) => setField("rooms", v)} step={0.5} testId="quote-clean-rooms" /></Block>
      </Grid2>
      <Block label={xe.clean.optionsLabel} hint={f.selectMultiple}>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="quote-clean-options">
          {options.map(([id, label]) => {
            const on = (d.cleanOptions || []).includes(id);
            const IconMap = { windows: Sparkles, appliances: Wrench, balcony: Package, cellar: Boxes, handover: Check };
            return (
              <OptionRow key={id} icon={IconMap[id] || Check} label={label} active={on} onClick={() => toggleOpt(id)} testId={`quote-clean-opt-${id}`} />
            );
          })}
        </div>
      </Block>
      <Block label={xe.clean.stateLabel}>
        <DDSelect value={d.cleanState} onChange={(v) => setField("cleanState", v)} options={states} placeholder={x.qf.choosePh || "…"} testId="quote-clean-state" />
      </Block>
    </>
  );
}

function RecycleFields({ d, setField, x }) {
  const f = x.qf.f;
  return (
    <>
      <Block label={f.recItems}><Textarea value={d.itemsType} onChange={(v) => setField("itemsType", v)} placeholder={f.recItemsPh} testId="quote-rec-items" /></Block>
      <Grid2>
        <Block label={f.surfApprox}><Field value={d.surface} onChange={(v) => setField("surface", v)} placeholder="20" testId="quote-rec-surface" inputMode="numeric" /></Block>
        <Block label={f.floor}><Stepper value={d.floorFrom} onChange={(v) => setField("floorFrom", v)} testId="quote-rec-floor" /></Block>
      </Grid2>
    </>
  );
}

function AssemblyFields({ d, setField, x }) {
  const f = x.qf.f;
  const kinds = [
    { id: "assembly", label: f.asbKinds.assembly },
    { id: "disassembly", label: f.asbKinds.disassembly },
    { id: "both", label: f.asbKinds.both },
  ];
  const brands = [{id:"ikea",label:f.brands.ikea},{id:"autre",label:f.brands.autre},{id:"mix",label:f.brands.mix}];
  return (
    <>
      <Block label={f.asbKind}>
        <Grid2>
          {kinds.map((o) => (
            <Pill key={o.id} active={d.assemblyKind === o.id} onClick={() => setField("assemblyKind", o.id)} testId={`quote-asb-${o.id}`}>
              <div className="font-bold text-sm">{o.label}</div>
            </Pill>
          ))}
        </Grid2>
      </Block>
      <Block label={f.brand}>
        <Grid2>
          {brands.map((o) => (
            <Pill key={o.id} active={d.brand === o.id} onClick={() => setField("brand", o.id)} testId={`quote-brand-${o.id}`}>
              <div className="font-bold text-sm">{o.label}</div>
            </Pill>
          ))}
        </Grid2>
      </Block>
      <Block label={f.nbFurn}><Stepper value={d.itemCount} onChange={(v) => setField("itemCount", v)} testId="quote-asb-count" min={1} /></Block>
    </>
  );
}

function FullFields({ d, toggleFull, x }) {
  const subs = SERVICE_KEYS.filter((k) => k !== "full").map((k) => ({ id: k, label: x.qf.services[k].label, Icon: SERVICE_ICONS[k] }));
  return (
    <Block label={x.qf.f.fullSubsLabel} hint={x.qf.f.fullHint}>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="quote-full-list">
        {subs.map((s) => (
          <OptionRow key={s.id} icon={s.Icon} label={s.label} active={d.fullSubservices.includes(s.id)} onClick={() => toggleFull(s.id)} testId={`quote-full-${s.id}`} />
        ))}
      </div>
    </Block>
  );
}

/* ---------- UI atoms ---------- */
function Card({ children }) {
  return <section className="rounded-3xl bg-white p-6 sm:p-8 md:p-10 shadow-[0_25px_50px_-25px_rgba(10,15,44,0.18)]">{children}</section>;
}

function ProgressBar({ step, total, labels }) {
  return (
    <div className="mb-6" data-testid="quote-progress">
      <div className="flex items-center gap-2">
        {Array.from({ length: total }).map((_, i) => (
          <div key={i} className={`h-1.5 rounded-full transition-all flex-1 ${step >= i ? "bg-[#2D5BFF]" : "bg-gray-200"}`} />
        ))}
      </div>
      {labels && (
        <div className="mt-2 grid text-[10px] uppercase tracking-[0.15em] font-bold text-[#9CA3AF]" style={{ gridTemplateColumns: `repeat(${total}, minmax(0, 1fr))` }}>
          {labels.map((lb, i) => (
            <div key={i} className={`text-center truncate ${step >= i ? "text-[#2D5BFF]" : ""}`}>{lb}</div>
          ))}
        </div>
      )}
    </div>
  );
}

function SectionTitle({ eyebrow, title, sub }) {
  return (
    <div>
      {eyebrow && <div className="text-xs uppercase tracking-[0.22em] text-[#2D5BFF] font-bold mb-2">{eyebrow}</div>}
      <h2 className="text-3xl md:text-5xl font-extrabold tracking-tighter text-[#0A0F2C] leading-[0.95]" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{title}</h2>
      {sub && <p className="mt-3 text-[#4B5563] md:text-lg">{sub}</p>}
    </div>
  );
}

function ChoiceCard({ active, onClick, Icon, label, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`relative text-left rounded-2xl border-2 p-4 md:p-5 transition-all ${active ? "border-[#2D5BFF] bg-[#EAF0FF]" : "border-gray-200 bg-white hover:border-gray-300"}`}
      data-testid={testId}
    >
      <Icon size={22} className={active ? "text-[#2D5BFF]" : "text-[#0A0F2C]"} />
      <div className="mt-3 font-extrabold text-sm md:text-base text-[#0A0F2C]" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{label}</div>
      {active && <span className="absolute top-3 right-3 w-5 h-5 rounded-full bg-[#2D5BFF] text-white inline-flex items-center justify-center"><Check size={12} strokeWidth={3} /></span>}
    </button>
  );
}

function Block({ label, hint, children }) {
  return (
    <div>
      <div className="flex items-baseline justify-between gap-2 mb-2">
        <span className="text-sm font-bold text-[#0A0F2C]">{label}</span>
        {hint && <span className="text-xs text-[#9CA3AF]">{hint}</span>}
      </div>
      {children}
    </div>
  );
}

function Grid2({ children }) { return <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">{children}</div>; }

function Pill({ active, onClick, children, testId }) {
  return (
    <button type="button" onClick={onClick} className={`text-left rounded-xl border-2 px-4 py-3 transition-all ${active ? "border-[#2D5BFF] bg-[#EAF0FF] text-[#0A0F2C]" : "border-gray-200 bg-white text-[#0A0F2C] hover:border-gray-300"}`} data-testid={testId}>
      {children}
    </button>
  );
}

/**
 * OptionRow — compact selectable line with icon left, label, checkmark right.
 * Used for "Options complémentaires" and "Contrainte d'accès".
 */
function OptionRow({ icon: Icon, label, active, onClick, testId }) {
  return (
    <button
      type="button" onClick={onClick} data-testid={testId}
      className={`w-full flex items-center gap-3 rounded-xl border transition-all px-3.5 py-2.5 text-left
        ${active ? "border-[#2D5BFF] bg-[#EAF0FF]" : "border-gray-200 bg-white hover:border-gray-300"}`}
    >
      <span className={`w-8 h-8 shrink-0 rounded-lg flex items-center justify-center transition-colors
        ${active ? "bg-[#2D5BFF] text-white" : "bg-gray-100 text-[#4B5563]"}`}>
        {Icon ? <Icon size={15} /> : null}
      </span>
      <span className="flex-1 text-sm font-semibold text-[#0A0F2C] leading-tight">{label}</span>
      <span className={`w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 transition-all
        ${active ? "border-[#2D5BFF] bg-[#2D5BFF] text-white" : "border-gray-300 bg-white text-transparent"}`}>
        <Check size={12} strokeWidth={3} />
      </span>
    </button>
  );
}

function SegControl({ options, value, onChange, testIdPrefix }) {
  return (
    <div className="inline-flex rounded-xl bg-gray-100 p-1 w-full">
      {options.map((o) => (
        <button key={o.id} type="button" onClick={() => onChange(o.id)} className={`flex-1 rounded-lg px-3 py-2 text-sm font-bold transition-all ${value === o.id ? "bg-white text-[#0A0F2C] shadow-sm" : "text-[#4B5563]"}`} data-testid={`${testIdPrefix}-${o.id}`}>
          {o.label}
        </button>
      ))}
    </div>
  );
}

function Stepper({ value, onChange, min = 0, max = 30, step = 1, testId }) {
  const isDecimal = step < 1;
  const round = (n) => isDecimal ? Math.round(n * 2) / 2 : Math.round(n);
  const display = (n) => (isDecimal && n % 1 !== 0) ? String(n) : String(Math.round(n));
  return (
    <div className="inline-flex items-center rounded-xl border border-gray-200 bg-white" data-testid={testId}>
      <button type="button" onClick={() => onChange(Math.max(min, round((Number(value) || 0) - step)))} className="w-11 h-11 inline-flex items-center justify-center text-[#0A0F2C] hover:bg-gray-100 rounded-l-xl" aria-label="-">
        <Minus size={16} />
      </button>
      <input
        type="number"
        value={display(Number(value) || 0)}
        step={step}
        onChange={(e) => onChange(Math.max(min, Math.min(max, round(Number(e.target.value) || 0))))}
        className="w-14 h-11 text-center bg-transparent text-[#0A0F2C] font-bold outline-none"
        inputMode={isDecimal ? "decimal" : "numeric"}
      />
      <button type="button" onClick={() => onChange(Math.min(max, round((Number(value) || 0) + step)))} className="w-11 h-11 inline-flex items-center justify-center text-[#0A0F2C] hover:bg-gray-100 rounded-r-xl" aria-label="+">
        <Plus size={16} />
      </button>
    </div>
  );
}

function ToggleList({ items }) {
  return (
    <div className="flex flex-col gap-2">
      {items.map((it) => (
        <button key={it.id} type="button" onClick={it.on} className={`flex items-center justify-between gap-3 rounded-xl border-2 px-4 py-3 text-left transition-all ${it.checked ? "border-[#2D5BFF] bg-[#EAF0FF]" : "border-gray-200 bg-white hover:border-gray-300"}`} data-testid={`quote-toggle-${it.id}`}>
          <span className="font-medium text-sm text-[#0A0F2C]">{it.label}</span>
          <span className={`w-5 h-5 rounded-full inline-flex items-center justify-center shrink-0 ${it.checked ? "bg-[#2D5BFF] text-white" : "bg-gray-200 text-transparent"}`}>
            <Check size={12} strokeWidth={3} />
          </span>
        </button>
      ))}
    </div>
  );
}

function Field({ label, value, onChange, type = "text", required, className = "", testId, placeholder, autoFocus, inputMode }) {
  return (
    <label className={`block ${className}`}>
      {label && <span className="text-sm font-bold text-[#0A0F2C] mb-1.5 block">{label}{required && <span className="text-red-500"> *</span>}</span>}
      <input
        type={type}
        value={value}
        inputMode={inputMode}
        autoFocus={autoFocus}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        className="w-full h-12 rounded-xl border border-gray-200 bg-white px-4 text-[#0A0F2C] placeholder:text-gray-400 focus:ring-2 focus:ring-[#2D5BFF] outline-none"
        data-testid={testId}
      />
    </label>
  );
}

function Textarea({ value, onChange, placeholder, testId }) {
  return (
    <textarea
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      rows={3}
      className="w-full rounded-xl border border-gray-200 bg-white px-4 py-3 text-[#0A0F2C] placeholder:text-gray-400 focus:ring-2 focus:ring-[#2D5BFF] outline-none resize-none"
      data-testid={testId}
    />
  );
}

function NavBtns({ onBack, onNext, backLabel, nextLabel, testIdPrefix }) {
  return (
    <div className="mt-8 flex flex-col-reverse sm:flex-row justify-between gap-3">
      <button onClick={onBack} className="inline-flex items-center justify-center gap-2 rounded-full bg-gray-100 hover:bg-gray-200 text-[#0A0F2C] px-6 py-3 font-bold transition-all" data-testid={`${testIdPrefix}-back`}>
        <ArrowLeft size={16} /> {backLabel}
      </button>
      <button onClick={onNext} className="inline-flex items-center justify-center gap-2 rounded-full bg-[#0A0F2C] hover:bg-[#2D5BFF] text-white px-6 py-3 font-bold transition-all" data-testid={`${testIdPrefix}-next`}>
        {nextLabel} <ArrowRight size={16} />
      </button>
    </div>
  );
}

function Row({ k, v, className = "" }) {
  return (
    <div className={`flex flex-col gap-1 ${className}`}>
      <dt className="text-[10px] uppercase tracking-[0.18em] text-[#9CA3AF] font-bold">{k}</dt>
      <dd className="text-[#0A0F2C] font-medium break-words">{v || "—"}</dd>
    </div>
  );
}

/* ---------- Helpers ---------- */
function titleForService(s, x) {
  return x.qf.titles[s] || x.qf.titles.default;
}

function subForService(s, x) {
  return x.qf.subs[s] || "";
}

function summaryRows(service, d, x, SERVICES) {
  const k = x.qf.keys;
  const rows = [];
  const liftLabel = (v) => (v === "yes" ? x.common.yes : v === "no" ? x.common.no : "");
  if (service === "move") {
    rows.push({ k: k.depart, v: d.addressFrom });
    rows.push({ k: k.arrive, v: d.addressTo });
    if (d.rooms) rows.push({ k: k.rooms, v: d.rooms });
    if (d.surface) rows.push({ k: k.volume, v: `${d.surface} m³` });
    rows.push({ k: k.floorFrom, v: d.floorFrom ? `${d.floorFrom} (${liftLabel(d.liftFrom)})` : "" });
    rows.push({ k: k.floorTo, v: d.floorTo ? `${d.floorTo} (${liftLabel(d.liftTo)})` : "" });
    const opts = [d.needBoxes && k.boxes, d.needDisassembly && k.disassembly, d.needCleaning && k.cleaning].filter(Boolean).join(", ");
    if (opts) rows.push({ k: k.options, v: opts });
  }
  if (service === "transport") {
    rows.push({ k: k.item, v: d.itemDescription });
    if (d.dimensions) rows.push({ k: k.dim, v: d.dimensions });
    rows.push({ k: k.depart, v: d.addressFrom });
    rows.push({ k: k.arrive, v: d.addressTo });
    if (d.needDisassembly) rows.push({ k: k.options, v: k.disassembly });
  }
  if (service === "clear") {
    rows.push({ k: k.space, v: d.spaceType });
    if (d.surface) rows.push({ k: k.volume, v: `${d.surface} m³` });
    if (d.floorFrom) rows.push({ k: k.floor, v: `${d.floorFrom} (${liftLabel(d.liftFrom)})` });
    const opts = [d.heavyItems && k.heavy, d.cleanAfter && k.cleanAfter].filter(Boolean).join(", ");
    if (opts) rows.push({ k: k.precisions, v: opts });
  }
  if (service === "declutter") { rows.push({ k: k.room, v: d.targetRoom }); rows.push({ k: k.goal, v: d.goal }); }
  if (service === "organize") {
    rows.push({ k: k.room, v: d.targetRoom });
    rows.push({ k: k.goal, v: d.goal });
    const opts = [d.needStorageBuy && k.purchases, d.needDeclutterFirst && k.decFirst].filter(Boolean).join(", ");
    if (opts) rows.push({ k: k.options, v: opts });
  }
  if (service === "clean") {
    rows.push({ k: k.type, v: d.cleanType });
    if (d.surface) rows.push({ k: k.surface, v: `${d.surface} m²` });
    if (d.rooms) rows.push({ k: k.rooms, v: d.rooms });
    const opts = [d.windows && k.win, d.appliances && k.appl, d.finalLease && k.finalLease].filter(Boolean).join(", ");
    if (opts) rows.push({ k: k.options, v: opts });
  }
  if (service === "recycle") {
    rows.push({ k: k.items, v: d.itemsType });
    if (d.surface) rows.push({ k: k.surface, v: `${d.surface} m²` });
  }
  if (service === "assembly") {
    rows.push({ k: k.type, v: x.qf.f.asbKinds[d.assemblyKind] });
    rows.push({ k: k.brand, v: d.brand.toUpperCase() });
    rows.push({ k: k.furn, v: d.itemCount });
  }
  if (service === "full") {
    rows.push({ k: k.included, v: d.fullSubservices.map((s) => SERVICES[s]?.label).join(", ") });
  }
  return rows;
}

function buildPayload(service, d, contact, lang) {
  // Build a service-specific `details` dict containing only the fields used by this service.
  const details = {};
  const add = (k, v) => {
    if (v === undefined || v === null || v === "") return;
    details[k] = v;
  };

  if (service === "move") {
    add("addressFrom", d.addressFrom);
    add("addressTo", d.addressTo);
    add("rooms", Number(d.rooms) || 0);
    add("surface", d.surface);
    add("floorFrom", Number(d.floorFrom) || 0);
    add("floorTo", Number(d.floorTo) || 0);
    add("liftFrom", d.liftFrom);
    add("liftTo", d.liftTo);
    add("needBoxes", d.needBoxes);
    add("needDisassembly", d.needDisassembly);
    add("needCleaning", d.needCleaning);
  } else if (service === "transport") {
    add("itemDescription", d.itemDescription);
    add("dimensions", d.dimensions);
    add("addressFrom", d.addressFrom);
    add("addressTo", d.addressTo);
    add("floorFrom", Number(d.floorFrom) || 0);
    add("floorTo", Number(d.floorTo) || 0);
    add("needDisassembly", d.needDisassembly);
  } else if (service === "clear") {
    add("spaceType", d.spaceType);
    add("surface", d.surface);
    add("floorFrom", Number(d.floorFrom) || 0);
    add("liftFrom", d.liftFrom);
    add("heavyItems", d.heavyItems);
    add("cleanAfter", d.cleanAfter);
  } else if (service === "declutter") {
    add("targetRoom", d.targetRoom);
    add("goal", d.goal);
  } else if (service === "organize") {
    add("targetRoom", d.targetRoom);
    add("goal", d.goal);
    add("needStorageBuy", d.needStorageBuy);
    add("needDeclutterFirst", d.needDeclutterFirst);
  } else if (service === "clean") {
    add("cleanType", d.cleanType);
    add("surface", d.surface);
    add("rooms", Number(d.rooms) || 0);
    add("windows", d.windows);
    add("appliances", d.appliances);
    add("finalLease", d.finalLease);
  } else if (service === "recycle") {
    add("itemsType", d.itemsType);
    add("surface", d.surface);
    add("floorFrom", Number(d.floorFrom) || 0);
  } else if (service === "assembly") {
    add("assemblyKind", d.assemblyKind);
    add("brand", d.brand);
    add("itemCount", Number(d.itemCount) || 1);
  } else if (service === "full") {
    add("fullSubservices", d.fullSubservices);
  }

  // Additional per-service qualification (enhancement 2026-02) — augment prior branches
  if (service === "move") {
    add("homeSize", d.homeSize);
    add("moveExtras", d.moveExtras);
    add("accessConstraint", d.accessConstraint);
  }

  if (service === "clear") {
    add("clearVolume", d.clearVolume);
    add("clearSituation", d.clearSituation);
    add("heavyChoice", d.heavyChoice);
  }
  if (service === "clean") {
    add("cleanOptions", d.cleanOptions);
    add("cleanState", d.cleanState);
  }
  if (service === "organize") {
    add("organizeScope", d.organizeScope);
  }
  if (service === "transport") {
    add("transportItem", d.transportItem);
    add("transportHeavy", d.transportHeavy);
    add("transportDis", d.transportDis);
  }

  return {
    service,
    client_type: d.clientType,
    company_name: (contact.companyName || "").trim() || null,
    language: lang || null,
    urgency: d.urgency,
    details,
    first_name: contact.firstName.trim(),
    last_name: (contact.lastName || "").trim(),
    email: (contact.email || "").trim() || null,
    phone: contact.phone.trim(),
    address: (contact.address || "").trim(),
    postal_code: contact.postalCode || null,
    preferred_date: contact.date || null,
    time_slot: contact.timeSlot || null,
    message: d.message || null,
    photo_ids: Array.isArray(d.photo_ids) ? d.photo_ids : [],
    callback_preference: contact.callback || null,
    contact_method: contact.contactMethod || null,
    combined_services: Array.isArray(contact.combinedServices) ? contact.combinedServices : [],
    opt_in: !!contact.optIn,
  };
}
